import {
    getResourceFlowMetric,
    getResourceFlowMetricSettings,
    withFlowUserType,
} from 'src/app/dashboards/mopa-resource-flow/resource-flow.models';
import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import {
    FlowDashboardData,
    getFlowAccumulation,
    getFlowAxis,
} from 'src/app/services/flow-dashboard/flow-dashboard.models';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardResourcesSqlMap } from '../../mopa-resources-detailed/sql-maps/biviz-dashboard-resources-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

/**
 * Resource Flow on a cohort lifetime-day axis (Install Day or Active Day).
 *
 * This is a cohort view, not a re-pivot of the by-date data: `ACTIVITY_DATE` and the app-version
 * columns are ignored, and the cohort comes from `FIREBASE_FIRST_OPEN_DATE`, exactly as in the
 * classic `resources-by-day-no` / `deco-activity-by-day-no` loaders.
 *
 * RUNNING is a cumulative sum of the daily values, including for the non-additive modes — parity with
 * the existing `running_median_*` dashboards. A "Running P50" is therefore the running total of daily
 * medians and is not itself a median; the chart documentation says so.
 */

/** Ignored on cohort axes. */
const IGNORED_ON_DAY_NO_AXIS = [
    'ACTIVITY_DATE',
    'ACTIVITY_APP_VERSION',
    'ACTIVITY_APP_VERSION_GROUP_MINOR',
];

export interface ResourceFlowByDayNoDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

export class ResourceFlowByDayNoDataLoaderFactory extends Factory<
    DataLoader,
    ResourceFlowByDayNoDataLoaderFactoryOptions
> {
    override FACTORY_CLASS = 'ResourceFlowByDayNoDataLoaderFactory';

    getInstance(options: ResourceFlowByDayNoDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                const axis = getFlowAxis(queryContext);
                if (axis === 'date') {
                    return;
                }
                const isActiveDayAxis = axis === 'active_day';

                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const resourcesSqlMap = BiVizDashboardResourcesSqlMap.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    resourcesSqlMap.columns
                );
                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);
                dsColumns.present = dsColumns.present.filter(
                    (column) => !IGNORED_ON_DAY_NO_AXIS.includes(column.id)
                );

                const isRunning =
                    getFlowAccumulation(queryContext) === 'running';
                const maxDayNoSQL = '90';
                const resourcesSQL =
                    BiVizDashboardResourcesSqlMap.sqlStatementsCompiler.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options
                    );

                const currency =
                    queryContext.settings[
                        QueryContextSettingID.RESOURCE_FLOW_CURRENCY
                    ] ?? 'gems';
                const { valueMode, population, percentiles } =
                    getResourceFlowMetricSettings(queryContext);
                const metric = getResourceFlowMetric(queryContext);

                if (valueMode === 'percentile' && !percentiles.length) {
                    throw new Error(
                        'Resource Flow: percentile mode needs at least one selected percentile'
                    );
                }

                const splitsByPercentile =
                    valueMode === 'percentile' && percentiles.length > 1;

                // Per-DAU population is independent of resource-specific flow filters.
                const usersdashActivity = BiVizDashboardUsersActivity;
                const usersdashActivityDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        usersdashActivity.sqlMap.columns
                    );
                usersdashActivityDsColumns.present =
                    usersdashActivityDsColumns.present.filter(
                        (column) => !IGNORED_ON_DAY_NO_AXIS.includes(column.id)
                    );
                const usersdashActivitySQL =
                    usersdashActivity.compileSQLStatements(
                        usersdashActivityDsColumns.present,
                        queryContext.settings,
                        options
                    );

                const resourcesTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    resourcesSqlMap.dataset,
                    resourcesSqlMap.tableId
                );
                const usersdashActivityTablePath = generateTableFullPath(
                    options.gcpProjectId,
                    usersdashActivity.sqlMap.dataset,
                    usersdashActivity.sqlMap.tableId
                );

                // User Type filters flow and cohort users by the same daily flag.
                const userType =
                    queryContext.settings[
                        QueryContextSettingID.FLOW_USER_TYPE
                    ] ?? 'all';
                const resourcesWhere = withFlowUserType(
                    resourcesSQL.where,
                    userType,
                    'resources'
                );
                const usersActivityWhere = withFlowUserType(
                    usersdashActivitySQL.where,
                    userType,
                    'activity'
                );
                // Only typed per-DAU branches need real player-days; ALL keeps the legacy grid.
                const usesTypedDauPopulation =
                    userType !== 'all' &&
                    population === 'dau' &&
                    (valueMode === 'average' || valueMode === 'percentile');

                let flowAggregationSQL: string;

                if (valueMode === 'total') {
                    flowAggregationSQL = `
, flow_agg AS
(
  SELECT ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
    , SUM(${currency}_inflow)  AS inflow
    , SUM(${currency}_outflow) AS outflow
  FROM base
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
)`;
                } else if (valueMode === 'flow records') {
                    flowAggregationSQL = `
, flow_agg AS
(
  SELECT ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
    , COUNTIF(${currency}_inflow > 0)  AS inflow
    , COUNTIF(${currency}_outflow > 0) AS outflow
  FROM base
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
)`;
                } else if (valueMode === 'players') {
                    flowAggregationSQL = `
, flow_agg AS
(
  SELECT ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
    , COUNT(DISTINCT IF(${currency}_inflow > 0, player_id, NULL))  AS inflow
    , COUNT(DISTINCT IF(${currency}_outflow > 0, player_id, NULL)) AS outflow
  FROM base
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
)`;
                } else if (valueMode === 'average' && population === 'deu') {
                    // Each side divides by its own engaged players on that cohort day.
                    flowAggregationSQL = `
, flow_agg AS
(
  SELECT ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
    , SAFE_DIVIDE(
        SUM(${currency}_inflow),
        COUNT(DISTINCT IF(${currency}_inflow > 0, player_id, NULL))
      ) AS inflow
    , SAFE_DIVIDE(
        SUM(${currency}_outflow),
        COUNT(DISTINCT IF(${currency}_outflow > 0, player_id, NULL))
      ) AS outflow
  FROM base
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
)`;
                } else if (valueMode === 'average' && population === 'dau') {
                    // Install Day uses D0; Active Day uses players who reached the day.
                    flowAggregationSQL = `
, cohort_sizes AS
(
  SELECT
    ${resourcesSQL.splitGroupBy}day_no
    , SUM(cohort_size) AS cohort_size
  FROM
  (
    SELECT
      ${resourcesSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
      first_open_date
      , day_no
      , COUNT(DISTINCT player_id) AS cohort_size
    ${
        usesTypedDauPopulation
            ? 'FROM typed_days'
            : `FROM cohort
    CROSS JOIN UNNEST(GENERATE_ARRAY(0, ${
        isActiveDayAxis
            ? `COALESCE(LEAST(max_active_day_no, ${maxDayNoSQL}), 0)`
            : maxDayNoSQL
    })) AS day_no`
    }
    ${
        resourcesSQL.is_in_context_split_datasource_specific_columns
            ? `CROSS JOIN (SELECT DISTINCT ${resourcesSQL.datasourceSpecificSplitGroupAlias} FROM base)`
            : ''
    }
    GROUP BY ${resourcesSQL.splitGroupBy}first_open_date, day_no
  )
  GROUP BY ${resourcesSQL.splitGroupBy}day_no
)
, resources_agg AS
(
  SELECT ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
    , SUM(${currency}_inflow)  AS sum_inflow
    , SUM(${currency}_outflow) AS sum_outflow
  FROM base
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
)
, flow_agg AS
(
  SELECT
    ${
        resourcesSQL.splitGroup
            ? 'resources_agg.split_group AS split_group, '
            : ''
    }${
        resourcesSQL.bucketGroup
            ? 'resources_agg.bucket_group AS bucket_group, '
            : ''
    }resources_agg.day_no AS day_no
    , SAFE_DIVIDE(resources_agg.sum_inflow, cohort_sizes.cohort_size)  AS inflow
    , SAFE_DIVIDE(resources_agg.sum_outflow, cohort_sizes.cohort_size) AS outflow
  FROM resources_agg
  LEFT JOIN cohort_sizes
    ON resources_agg.day_no = cohort_sizes.day_no
    ${
        resourcesSQL.splitGroup
            ? "AND IFNULL(resources_agg.split_group, '_NULL_') = IFNULL(cohort_sizes.split_group, '_NULL_')"
            : ''
    }
)`;
                } else if (valueMode === 'percentile' && population === 'deu') {
                    // A percentile describes one player, so rows are summed per player and cohort
                    // day first. `deu` keeps only the players with a positive flow on that side.
                    flowAggregationSQL = `
, per_player AS
(
  SELECT ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
    , player_id
    , SUM(${currency}_inflow)  AS inflow
    , SUM(${currency}_outflow) AS outflow
  FROM base
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no, player_id
)
, flow_agg AS
(
  SELECT ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
    , APPROX_QUANTILES(IF(inflow > 0, inflow, NULL), 100)[OFFSET(${percentiles[0]})]   AS inflow
    , APPROX_QUANTILES(IF(outflow > 0, outflow, NULL), 100)[OFFSET(${percentiles[0]})] AS outflow${
        splitsByPercentile
            ? percentiles
                  .map(
                      (percentile) => `
    , APPROX_QUANTILES(IF(inflow > 0, inflow, NULL), 100)[OFFSET(${percentile})]   AS quantile_inflow_${percentile}
    , APPROX_QUANTILES(IF(outflow > 0, outflow, NULL), 100)[OFFSET(${percentile})] AS quantile_outflow_${percentile}`
                  )
                  .join('')
            : ''
    }
  FROM per_player
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
)`;
                } else if (valueMode === 'percentile' && population === 'dau') {
                    flowAggregationSQL = `
, per_player AS
(
  SELECT ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
    , player_id
    , SUM(${currency}_inflow)  AS inflow
    , SUM(${currency}_outflow) AS outflow
  FROM base
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no, player_id
)
, cohort_players AS
(
  SELECT
    ${resourcesSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    day_no
    , player_id
  FROM
  (
    SELECT ${resourcesSQL.cohortSplitGroupBy}d.day_no AS day_no
      , c.player_id AS player_id
    FROM days d
    ${
        usesTypedDauPopulation
            ? 'JOIN typed_days c ON c.day_no = d.day_no'
            : isActiveDayAxis
              ? 'JOIN cohort c ON c.max_active_day_no >= d.day_no'
              : 'CROSS JOIN cohort c'
    }
  )
  ${
      resourcesSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${resourcesSQL.datasourceSpecificSplitGroupAlias} FROM base)`
          : ''
  }
  ${
      resourcesSQL.bucketGroup
          ? 'CROSS JOIN (SELECT DISTINCT bucket_group FROM base)'
          : ''
  }
)
, population AS
(
  SELECT ${
      resourcesSQL.splitGroup
          ? 'cohort_players.split_group AS split_group, '
          : ''
  }${
      resourcesSQL.bucketGroup
          ? 'cohort_players.bucket_group AS bucket_group, '
          : ''
  }cohort_players.day_no AS day_no
    , COALESCE(per_player.inflow, 0)  AS inflow
    , COALESCE(per_player.outflow, 0) AS outflow
  FROM cohort_players
  LEFT JOIN per_player
    ON per_player.day_no = cohort_players.day_no
   AND per_player.player_id = cohort_players.player_id
   ${
       /* A Split or Group over a nullable column yields a NULL group, which plain equality would
          never match: the whole group would read as zero flow. */
       resourcesSQL.splitGroup
           ? `AND IFNULL(per_player.split_group, '_NULL_') = IFNULL(cohort_players.split_group, '_NULL_')`
           : ''
   }
   ${
       resourcesSQL.bucketGroup
           ? `AND IFNULL(per_player.bucket_group, '_NULL_') = IFNULL(cohort_players.bucket_group, '_NULL_')`
           : ''
   }
)
, flow_agg AS
(
  SELECT ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
    , APPROX_QUANTILES(inflow, 100)[OFFSET(${percentiles[0]})]  AS inflow
    , APPROX_QUANTILES(outflow, 100)[OFFSET(${percentiles[0]})] AS outflow${
        splitsByPercentile
            ? percentiles
                  .map(
                      (percentile) => `
    , APPROX_QUANTILES(inflow, 100)[OFFSET(${percentile})]  AS quantile_inflow_${percentile}
    , APPROX_QUANTILES(outflow, 100)[OFFSET(${percentile})] AS quantile_outflow_${percentile}`
                  )
                  .join('')
            : ''
    }
  FROM population
  GROUP BY ${resourcesSQL.splitGroupBy}${resourcesSQL.bucketGroupBy}day_no
)`;
                } else {
                    // A new value mode or population must state its own query instead of silently
                    // inheriting another one.
                    throw new Error(
                        `Resource Flow: no lifetime-day query defined for value mode '${valueMode}' and population '${population}'`
                    );
                }

                const query = `
-- DataLoaderId: ${this.instanceId}
-- Resource Flow (${
                    isActiveDayAxis ? 'active day' : 'install day'
                } axis) - currency: ${currency}, mode: ${
                    metric.unit === 'currency' ? valueMode : metric.unit
                }${
                    valueMode === 'average' || valueMode === 'percentile'
                        ? ` per ${population}`
                        : ''
                }, accumulation: ${isRunning ? 'running' : 'daily'}
WITH cohort AS
(
  SELECT
${
    usersdashActivitySQL.splitGroupBy
        ? usersdashActivitySQL.splitSelect.replace(
              'AS split_group',
              'AS cohort_split_group'
          )
        : ''
}
    player_id
    , ${resourcesSqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL} AS first_open_date
    ${isActiveDayAxis ? ', MAX(ds.active_day_no) AS max_active_day_no' : ''}
  FROM \`${usersdashActivityTablePath}\` ds
  ${usersdashActivitySQL.join}
  ${usersdashActivitySQL.where}
  GROUP BY ${resourcesSQL.cohortSplitGroupBy}${
      resourcesSqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL
  }, player_id
)
, days AS
(
  SELECT day_no
  FROM UNNEST(GENERATE_ARRAY(0, ${maxDayNoSQL})) AS day_no
)${
                    usesTypedDauPopulation
                        ? `
, typed_days AS
(
  SELECT DISTINCT
${
    usersdashActivitySQL.splitGroupBy
        ? usersdashActivitySQL.splitSelect.replace(
              'AS split_group',
              'AS cohort_split_group'
          )
        : ''
}
      ds.${isActiveDayAxis ? 'active_day_no' : 'install_day_no'} AS day_no
    , ds.player_id
    , ${
        resourcesSqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL
    } AS first_open_date
  FROM \`${usersdashActivityTablePath}\` ds
  ${usersdashActivitySQL.join}
  ${usersActivityWhere}
    AND ds.${
        isActiveDayAxis ? 'active_day_no' : 'install_day_no'
    } BETWEEN 0 AND ${maxDayNoSQL}
)`
                        : ''
                }
, base AS
(
  SELECT
    ${resourcesSQL.splitSelect}
    ${resourcesSQL.datasourceSpecificSplitSelect}
    ${resourcesSQL.cohortSplitSelect}
    player_day.${isActiveDayAxis ? 'active_day_no' : 'install_day_no'} AS day_no
    , ds.player_id
    , ds.${currency}_inflow
    , ds.${currency}_outflow
    ${resourcesSQL.bucketSelect ? `, ${resourcesSQL.bucketSelect}` : ''}
  FROM \`${resourcesTablePath}\` ds
  ${resourcesSQL.join}
  ${resourcesWhere}
)
${flowAggregationSQL}
, grid AS
(
  SELECT
    ${resourcesSQL.splitGroup ? 'g.split_group, ' : ''}${
        resourcesSQL.bucketGroup ? 'g.bucket_group, ' : ''
    }d.day_no AS day_no
  FROM days d
  ${
      resourcesSQL.splitGroup || resourcesSQL.bucketGroup
          ? `CROSS JOIN
  (
    SELECT DISTINCT
      ${resourcesSQL.splitGroup ? 'split_group' : ''}${
          resourcesSQL.splitGroup && resourcesSQL.bucketGroup ? ', ' : ''
      }${resourcesSQL.bucketGroup ? 'bucket_group' : ''}
    FROM flow_agg
  ) g`
          : ''
  }
)

SELECT
    ${resourcesSQL.splitGroup ? 'grid.split_group AS split_group\n  , ' : ''}${
        resourcesSQL.bucketGroup
            ? 'grid.bucket_group AS bucket_group\n  , '
            : ''
    }CAST(grid.day_no AS STRING) AS day_no
  , ${
      isRunning
          ? `ROUND(SUM(COALESCE(flow_agg.inflow, 0)) OVER (${
                resourcesSQL.splitGroup
                    ? `PARTITION BY grid.split_group${
                          resourcesSQL.bucketGroup ? ', grid.bucket_group' : ''
                      } `
                    : resourcesSQL.bucketGroup
                      ? 'PARTITION BY grid.bucket_group '
                      : ''
            }ORDER BY grid.day_no), 2)`
          : 'ROUND(COALESCE(flow_agg.inflow, 0), 2)'
  } AS inflow
  , ${
      isRunning
          ? `ROUND(SUM(COALESCE(flow_agg.outflow, 0)) OVER (${
                resourcesSQL.splitGroup
                    ? `PARTITION BY grid.split_group${
                          resourcesSQL.bucketGroup ? ', grid.bucket_group' : ''
                      } `
                    : resourcesSQL.bucketGroup
                      ? 'PARTITION BY grid.bucket_group '
                      : ''
            }ORDER BY grid.day_no), 2)`
          : 'ROUND(COALESCE(flow_agg.outflow, 0), 2)'
  } AS outflow${
      splitsByPercentile
          ? percentiles
                .map(
                    (percentile) => `
  , ${
      isRunning
          ? `ROUND(SUM(COALESCE(flow_agg.quantile_inflow_${percentile}, 0)) OVER (${
                resourcesSQL.splitGroup
                    ? `PARTITION BY grid.split_group${
                          resourcesSQL.bucketGroup ? ', grid.bucket_group' : ''
                      } `
                    : resourcesSQL.bucketGroup
                      ? 'PARTITION BY grid.bucket_group '
                      : ''
            }ORDER BY grid.day_no), 2)`
          : `ROUND(COALESCE(flow_agg.quantile_inflow_${percentile}, 0), 2)`
  } AS quantile_inflow_${percentile}
  , ${
      isRunning
          ? `ROUND(SUM(COALESCE(flow_agg.quantile_outflow_${percentile}, 0)) OVER (${
                resourcesSQL.splitGroup
                    ? `PARTITION BY grid.split_group${
                          resourcesSQL.bucketGroup ? ', grid.bucket_group' : ''
                      } `
                    : resourcesSQL.bucketGroup
                      ? 'PARTITION BY grid.bucket_group '
                      : ''
            }ORDER BY grid.day_no), 2)`
          : `ROUND(COALESCE(flow_agg.quantile_outflow_${percentile}, 0), 2)`
  } AS quantile_outflow_${percentile}`
                )
                .join('')
          : ''
  }
FROM grid
LEFT JOIN flow_agg
  ON flow_agg.day_no = grid.day_no
  ${
      resourcesSQL.splitGroup
          ? "AND IFNULL(flow_agg.split_group, '_NULL_') = IFNULL(grid.split_group, '_NULL_')"
          : ''
  }
  ${
      resourcesSQL.bucketGroup
          ? "AND IFNULL(flow_agg.bucket_group, '_NULL_') = IFNULL(grid.bucket_group, '_NULL_')"
          : ''
  }
ORDER BY ${resourcesSQL.splitGroup ? 'grid.split_group, ' : ''}${
                    resourcesSQL.bucketGroup ? 'grid.bucket_group, ' : ''
                }grid.day_no
`;
                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                const resultRows = [...queryResponse.result];
                // The axis is densified in SQL, so there is no fillMissingTimeGapRows step here.
                const rows = splitsByPercentile
                    ? resultRows.flatMap((row) =>
                          percentiles.map((percentile) => {
                              const label = `P${percentile}`;
                              return {
                                  ...row,
                                  inflow: row[`quantile_inflow_${percentile}`],
                                  outflow:
                                      row[`quantile_outflow_${percentile}`],
                                  split_group: resourcesSQL.splitGroup
                                      ? `${row.split_group ?? 'Total'} · ${label}`
                                      : label,
                                  ...(resourcesSQL.bucketGroup
                                      ? {
                                            bucket_group: `${
                                                row.bucket_group ?? 'Unknown'
                                            } · ${label}`,
                                        }
                                      : {}),
                              };
                          })
                      )
                    : resultRows;

                const data: FlowDashboardData = {
                    rows,
                    rangeAggregation: metric.rangeAggregation,
                };
                this.latestData = data;
                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
